/*eslint no-console:1*/
import { currentBreakpoint } from '../lib/responsive.js';
import { isRetina } from '../lib/helper.js';

/**
 * Lazyloads the original image and handles the transition between the inital thumb and the final image and set a blurry background.
 * to get the right image for the current breakpoint, images get this JSON-like structure applied to a data-image-src attribute:
 * {
 *   'xs': {
 *     'normal': 'path/to/image.jpg',
 *     'retina': 'path/to/image@2x.jpg'
 *   },
 *   's': {
 *     'normal': 'path/to/image.jpg',
 *     'retina': 'path/to/image@2x.jpg'
 *   },
 *   'm': {
 *     'normal': 'path/to/image.jpg',
 *     'retina': 'path/to/image@2x.jpg'
 *   },
 *   'l': {
 *     'normal': 'path/to/image.jpg',
 *     'retina': 'path/to/image@2x.jpg'
 *   },
 *   'xl': {
 *     'normal': 'path/to/image.jpg',
 *     'retina': 'path/to/image@2x.jpg'
 *   },
 *   'xxl': {
 *     'normal': 'path/to/image.jpg',
 *     'retina': 'path/to/image@2x.jpg'
 *   }
 * }
 * @type {Class}
 */
export default class LazyLodingImage {

  /**
   * Gets all required nodes and image-sources
   * @param  {Node} element the node given by the component-handler
   */
  constructor(element) {

    this.el = element;

    this.imgEl = this.el.querySelector('.lazy-image');
    this.canvasEl = this.el.querySelector('.is-preload-canvas');
    this.canvasBgEl = this.el.querySelector('.is-blurred-background');
    this.dataBlurRadius = this.el.getAttribute('data-blur-radius');
    this.imageResolution = isRetina() ? 'hidpi' : 'normal';
    this.fallbackImageSrc = this.el.getAttribute('data-fallback-image-src');
    this.hasNoPreload = !this.el.classList.contains('has-preload');
    this.isFsk18 = this.el.classList.contains('is-fsk18');
    this.backCover = this.el.getAttribute('data-back-cover');

    if (this.imgEl && !this.el.classList.contains('tc-blurry-background-image')) {

      this.preloadImageSrc = this.imgEl.src;

      this.isLazyBgImage = false;
    } else if (this.el.getAttribute('data-preload-image-src')) {

      this.preloadImageSrc = this.el.getAttribute('data-preload-image-src');
      this.isLazyBgImage = true;
    }

    try {

      this.imageSrcSet = JSON.parse(this.el.getAttribute('data-image-src'));
    } catch (error) {

      window.console.log(error, 'can not get images source-set object');
    }

    this.prepareResponsiveImages();
    this.getResponsiveImage(true);

    if (this.backCover) {

      this.el.addEventListener('mouseover', (e) => {

        e.preventDefault();
      
        // fix for images that have bigger height on cover-back that cover-front
        this.imgEl.style.height = this.el.offsetHeight + 'px';

        this.imgEl.src = this.backCover;
      });

      this.el.addEventListener('mouseleave', (e) => {

        e.preventDefault();

        this.imgEl.src = this.imageSrc;
      });
    }

    return this;
  }

  addToManager() {
    this.init();
  }

  init() {

    this.getMainImage();
  }

  getResponsiveImage(force) {

    try {

      this.imageSrc = this.imageSrcSet[currentBreakpoint][this.imageResolution];

    } catch (error) {

      window.console.log(error);
    }

    if ((!this.imageSrc || !this.imageSrc.length) && this.fallbackImageSrc) {

      this.imageSrc = this.fallbackImageSrc;
    } else if ((!this.imageSrc || !this.imageSrc.length) && !this.fallbackImageSrc) {

      this.imageSrc = this.preloadImageSrc;
    }

    if (force && !this.hasNoPreload || force && this.isLazyBgImage) {

      this.initCanvas();
    }

    this.addToManager();
  }

  prepareResponsiveImages() {

    window.addEventListener('changeBreakpoint', this.onBreakpointChange.bind(this), false);
  }

  onBreakpointChange(currBreakpoint) {

    this.getResponsiveImage();
  }

  /**
   * Generates a new image-object with original-image-source
   */
  getMainImage() {

    if (!this.image) {

      this.image = new Image();
      //this.image.crossOrigin = 'Anonymous';
      this.image.addEventListener('load', this.onMainImageReady.bind(this), false);
    }

    this.image.src = this.imageSrc;
  }

  /**
   * This is the callback-function for the load-event of the original-image.
   * Sets the source of the image-node and initializes the canvas for the blurry background.
   */
  onMainImageReady() {

    // console.log('onmain', this.imageSrc);

    this.el.classList.remove('ratio');

    if (this.isLazyBgImage) {

      if (!this.el.classList.contains('is-blurred')) {

        this.el.style.backgroundImage = 'url(' + this.imageSrc + ')';
      } else {

        this.el.style.backgroundImage = 'url(' + this.preloadImageSrc + ')';
      }

      this.el.classList.add('image-loaded');
    } else {

      this.imgEl.src = '';

      /* prevent removing blurred overlay before high-quality-image is loaded */
      this.imgEl.addEventListener('load', e => {

        this.el.classList.add('image-loaded', 'blur-background');
      });

      this.imgEl.src = this.imageSrc;

      if (false && this.canvasEl && this.hasNoPreload && !(Modernizr && Modernizr.touchevents)) {

        this.canvasContext = this.canvasEl.getContext('2d');
        this.canvasWidth = this.canvasEl.width;
        this.canvasHeight = this.canvasEl.height;
        this.canvasContext.drawImage(this.imgEl, 0, 0, this.canvasWidth, this.canvasHeight);
      }
    }
  }

  /**
   * Handles the thumbnail-image in the foreground-canvas.
   */
  initCanvas() {

    this.preloadImage = new Image();

    this.preloadImage.addEventListener('load', this.onpreloadImageReady.bind(this), false);

    this.preloadImage.src = this.preloadImageSrc;
  }

  onpreloadImageReady() {

    if (false && this.canvasEl) {

      this.canvasContext = this.canvasEl.getContext('2d');
      this.canvasWidth = this.canvasEl.width;
      this.canvasHeight = this.canvasEl.height;
      this.canvasContext.drawImage(this.preloadImage, 0, 0, this.canvasWidth, this.canvasHeight);
    }
  }
};
